<template>
  <CardComponent :padding="false" class="gap-0">
    <div class="flex justify-between items-center p-6 border-b-2 border-grey-light-200">
      <div>
        <h3 class="font-semibold text-lg text-grey-light-900">Payment Plan Analysis</h3>
        <p class="text-grey-light-600 text-xs">Placed, Completed, Cancelled: last 12 months</p>
      </div>
      <Button
        size="icon"
        variant="ghost"
        type="button"
        class="border border-grey-light-200 h-12 w-12 rounded-lg flex justify-center items-center shadow-xs"
        @click="showSection === 'graph' ? (showSection = 'table') : (showSection = 'graph')"
      >
        <Table2 v-if="showSection === 'graph'" class="h-6 w-6" />
        <ChartNoAxesColumn v-else class="h-6 w-6" />
      </Button>
    </div>
    <Skeleton v-if="isLoading" class="h-full w-full" />
    <template v-else>
      <div class="p-4" v-if="showSection === 'graph'">
        <BarChart
          index="name"
          :data="graphData"
          :colors="['#079455', '#38A6B3', '#F04438']"
          :categories="['Completed', 'Placed', 'Cancelled']"
          :rounded-corners="4"
        />
      </div>
      <div class="pb-6" v-else-if="showSection === 'table'">
        <DataTable :columns="paymentPlanAnalysisColumns" :data="graphData" :show-pagination="false" />
      </div>
    </template>
  </CardComponent>
</template>

<script setup lang="ts">
import CardComponent from '@/components/merchant/CardComponent.vue';
import DataTable from '@/components/merchant/DataTable.vue';
import { Button } from '@/components/ui/button';
import { BarChart } from '@/components/ui/chart-bar';
import { Skeleton } from '@/components/ui/skeleton';
import { NetworkError } from '@/errors';
import type { MappedOrderGraphData, OrderGraphData } from '@/models/graphs';
import { getOrdersGraphData } from '@/services/graph';
import { useMerchantStore } from '@/stores/merchants';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import dayjs from 'dayjs';
import { ChartNoAxesColumn, Table2 } from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { paymentPlanAnalysisColumns } from './PaymentPlanAnalysisColumns';

const showSection = ref<'graph' | 'table'>('graph');
const isLoading = ref(true);
const graphData = ref<MappedOrderGraphData[]>([]);
const merchantStore = useMerchantStore();

onMounted(async () => await getPaymentPlanAnalysisData());

const getPaymentPlanAnalysisData = async () => {
  isLoading.value = true;

  try {
    const response = await getOrdersGraphData();
    const data = response.data.graph;

    if (Array.isArray(data)) {
      const filteredData = data.filter((item: OrderGraphData) => item._id !== 'OTHER');

      filteredData.sort((a: OrderGraphData, b: OrderGraphData) => {
        return dayjs(b.month).isBefore(dayjs(a.month)) ? -1 : 1;
      });

      graphData.value = filteredData.map((el: OrderGraphData) => {
        return {
          name: el.month,
          Placed: el.PLACED,
          Completed: el.COMPLETED,
          Cancelled: el.CANCELLED,
        };
      });
    }
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('payment-plan-analysis-graph', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('payment-plan-analysis-graph', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => merchantStore.merchant,
  async (newMerchant) => {
    if (newMerchant) {
      await getPaymentPlanAnalysisData();
    }
  },
);
</script>
